import React, { CSSProperties, useMemo, useState, useEffect } from 'react';
import C from './SpaceExplorerPanel.css';
import cc from 'classcat';
import SearchIcon from '@mui/icons-material/Search';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import Checkbox from '@mui/material/Checkbox';
import SearchInputField from '../search-input-field/SearchInputField';
import IconButton from '@mui/material/IconButton';
import { atom, useRecoilState } from 'recoil';
import { spaceFiltersAtom } from 'atoms/space-filters';
import Button from '@mui/material/Button';
import FlexRow from 'components/flex/FlexRow';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getWorldCorridorSectionNames,
  getSpaceSections,
  useSpacesQuery,
  getWorldCorridorSections,
} from 'atoms/spaces';
import NewSpacesIcon from '!!react-svg-loader!./icon--new-spaces.svg';
import MySpacesIcon from '!!react-svg-loader!./icon--my-spaces.svg';
import OtherSpacesIcon from '!!react-svg-loader!./icon--other-spaces.svg';
import ListItemIcon from '@mui/material/ListItemIcon';
import Avatar from '@mui/material/Avatar';
import { visitSpaceSectionEvt } from 'utils/events/space';
import { useEvt } from 'evt/hooks';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import ROUTES, { DashboardParams } from 'constants/routes';
import { timeout } from 'utils/async';
import { getThemeLocalizedNames, useThemeSelection } from 'atoms/space-themes';
import { Trans } from '@lingui/macro';
import { useCommunitySummaries } from 'atoms/community-summaries';
import { CommunityWithAggregates } from '@cocoplatform/coco-rtc-shared';
import * as CMR from 'constants/community-roles';
import { Box, Tooltip } from '@mui/material';
import CocoJamIcon from '!!react-svg-loader!../world-corridor-panel/icons/icon--coco-jam.svg';

const sectionIcons = {
  'coco-jams': CocoJamIcon,
  'new-spaces-to-join': NewSpacesIcon,
  'my-coco-spaces': MySpacesIcon,
  'explore-other-spaces': OtherSpacesIcon,
};

export const SpaceExplorerStateAtom = atom({
  key: 'SpaceExplorerStateAtom',
  default: getSpaceSections()[0].id,
});

function PanelListLink({
  activeSectionId,
  isSpacesPage,
  section,
  newSpacesToJoin,
  communityId,
}: {
  section: { id: string; label: string };
  activeSectionId: string;
  isSpacesPage: boolean;
  newSpacesToJoin: { count: number } | undefined;
  communityId: string | undefined;
}) {
  const Icon = sectionIcons[section.id];

  const disabled =
    section.id === 'new-spaces-to-join' && newSpacesToJoin?.count === 0;

  const history = useHistory();

  return (
    <Tooltip
      title={disabled ? <Trans>No new spaces to join currently.</Trans> : ''}
      placement='top'
    >
      <ListItem
        key={section.id}
        disabled={disabled}
        className={cc({
          [C.navListItem]: true,
          [C.active]: isSpacesPage && section.id === activeSectionId,
        })}
        onClick={async () => {
          if (disabled) return;
          if (!isSpacesPage) {
            history.push(`/app/dashboard/${communityId}/spaces`);
            await timeout(500);
          }
          visitSpaceSectionEvt.post({
            sectionId: section.id,
          });
        }}
        sx={
          section.id === 'coco-jams'
            ? {
              borderBottom: '1px solid var(--border-col)',
            }
            : {}
        }
      >
        {Icon && (
          <ListItemIcon style={{ minWidth: '20px', cursor: 'pointer' }}>
            <Avatar style={{ height: '30px', width: '30px' }}>
              <Icon style={{ height: '16px', width: '16px' }} />
            </Avatar>
          </ListItemIcon>
        )}
        <ListItemText style={{ paddingLeft: '10px', cursor: 'pointer' }}>
          {communityId === '$all'
            ? getWorldCorridorSectionNames(section.id)
            : section.label}
        </ListItemText>
      </ListItem>
    </Tooltip>
  );
}

export default function SpaceExplorerPanel(p: {
  className?: string;
  style?: CSSProperties;
}) {
  const [spaceFilters, setSpaceFilters] = useRecoilState(spaceFiltersAtom);
  const [themesExpanded, setThemesExpanded] = useState(true);
  const [searchingThemes, setSearchingThemes] = useState(false);
  const [activeSectionId, setActiveSectionId] = useRecoilState(
    SpaceExplorerStateAtom,
  );
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const { communityId } = useParams<DashboardParams>();

  useEvt((ctx) => {
    visitSpaceSectionEvt.attach(ctx, ({ sectionId }) => {
      setActiveSectionId(sectionId);
    });
  }, []);

  const isSpacesPage = routeMatch.path === ROUTES.COMMUNITY_DASHBOARD_SPACES;
  const {
    communities,
    syncState: summarySyncState,
    trigger,
  } = useCommunitySummaries({
    autoLoad: false,
  });
  const selectHostableCommunities = (
    communities: CommunityWithAggregates[],
  ) => {
    return communities.filter((it) => {
      return it.isActive && CMR.isGTE(it.currentUserRoles, 'MEMBER');
    });
  };

  const hostableCommunities = useMemo(() => {
    return selectHostableCommunities(communities);
  }, [communities]);

  const queryCommunityId = communityId === '$all' ? undefined : communityId;

  const {
    visibleThemes,
    listQuery: themeListQuery,
    setListQuery: setThemeListQuery,
  } = useThemeSelection({
    autoLoad: true,
    communityId: queryCommunityId,
  });

  const { data: newSpacesToJoin } = useSpacesQuery({
    communityId: queryCommunityId,
    sectionId: 'new-spaces-to-join',
    filter: ['new-spaces-to-join', 'all'],
    sortCriteria: '',
    sortDir: 'asc',
  });

  useEffect(() => {
    if (
      newSpacesToJoin?.count === 0 &&
      activeSectionId === 'new-spaces-to-join' &&
      communityId !== '$all'
    ) {
      setActiveSectionId('my-coco-spaces');
    }
  }, [newSpacesToJoin?.count, activeSectionId]);

  return (
    <div className={p.className} style={p.style}>
      {/*
      <Box>
        {[...(communityId === '$all' ? [] : getWorldCorridorSections())].map(
          (section) => (
            <PanelListLink
              key={section.id}
              section={section}
              activeSectionId={activeSectionId}
              isSpacesPage={isSpacesPage}
              newSpacesToJoin={newSpacesToJoin}
              communityId={communityId}
            />
          ),
        )}
      </Box>
      */}
      <header
        className={C.secondaryHeader}
        style={{
          padding: '10px 20px',
          marginTop: '20px',
        }}
      >
        {communityId === '$all' ? (
          hostableCommunities.length === 1 ? (
            <Trans>Spaces in your community</Trans>
          ) : (
            <Trans>Spaces in your communities</Trans>
          )
        ) : (
          <Trans>Spaces in your community</Trans>
        )}
      </header>
      <List>
        {[...getSpaceSections()].map((section) => (
          <PanelListLink
            key={section.id}
            section={section}
            activeSectionId={activeSectionId}
            isSpacesPage={isSpacesPage}
            newSpacesToJoin={newSpacesToJoin}
            communityId={communityId}
          />
        ))}
      </List>
      {/* {isSpacesPage && communityId !== '$all' && (
        <>
          <hr
            style={{
              margin: '10px 15px',
              border: '1px solid var(--color-selected)',
            }}
          />
          <FlexRow
            style={{ paddingLeft: '16px', marginTop: '20px' }}
            className={C.secondaryHeader}
          >
            <IconButton
              onClick={() => {
                setSearchingThemes((isSearching) => !isSearching);
              }}
              style={{
                marginRight: '0.5rem',
              }}
            >
              <SearchIcon
                style={{
                  color: 'var(--color-text)',
                  height: '16px',
                  width: '16px',
                }}
              />
            </IconButton>
            <div style={{ flexGrow: 1, fontSize: 'var(--font-size-primary)' }}>
              <Trans>Themes</Trans>
            </div>
            <Button
              variant='text'
              size='small'
              onClick={() => {
                setSpaceFilters((sf) => ({
                  ...sf,
                  selectedThemeIds: [],
                }));
                setThemeListQuery('');
              }}
            >
              <Trans>Reset</Trans>
            </Button>
            <IconButton
              onClick={() => {
                setThemesExpanded((expanded) => !expanded);
              }}
            >
              {themesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </FlexRow>
          {themesExpanded && (
            <>
              {searchingThemes && (
                <SearchInputField
                  style={{
                    margin: '10px 20px',
                    width: 'calc(100% - 40px)',
                  }}
                  value={themeListQuery}
                  onChange={(e) => {
                    setThemeListQuery(e.target.value);
                  }}
                  onClear={() => {
                    setThemeListQuery('');
                  }}
                />
              )}
              <List>
                {visibleThemes.map((theme) => {
                  const toggleSelection = () => {
                    setSpaceFilters((sf) => {
                      const selectedThemeIds = sf.selectedThemeIds.filter(
                        (id) => id !== theme.id,
                      );
                      if (
                        selectedThemeIds.length === sf.selectedThemeIds.length
                      ) {
                        selectedThemeIds.push(theme.id);
                      }
                      return {
                        ...sf,
                        selectedThemeIds,
                      };
                    });
                  };
                  return (
                    <ListItem
                      key={theme.id}
                      secondaryAction={
                        <Checkbox
                          checked={
                            spaceFilters.selectedThemeIds.indexOf(theme.id) >= 0
                          }
                          onChange={toggleSelection}
                          style={{ color: 'var(--color-subtext)' }}
                        />
                      }
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <ListItemText
                        style={{ padding: '0 10px 0 40px' }}
                        onClick={toggleSelection}
                      >
                        {getThemeLocalizedNames()[theme.name]}
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </>
      )} */}
    </div>
  );
}
